import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { ArrowLeft, Calendar, DollarSign, Briefcase, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
interface Campaign {
  id: string;
  name: string;
  brand: string;
  brandLogo?: string;
  status: 'active' | 'planning' | 'completed';
  deadline: string;
  budget: number;
  deliverables: {
    total: number;
    completed: number;
    pending: number;
  };
}
const mockCampaigns: Campaign[] = [{
  id: '1',
  name: 'Nike Summer Campaign',
  brand: 'Nike',
  status: 'active',
  deadline: '2024-06-15',
  budget: 5000,
  deliverables: {
    total: 4,
    completed: 1,
    pending: 3
  }
}, {
  id: '2',
  name: 'Adidas Fitness Series',
  brand: 'Adidas',
  status: 'planning',
  deadline: '2024-07-01',
  budget: 3000,
  deliverables: {
    total: 3,
    completed: 0,
    pending: 3
  }
}, {
  id: '3',
  name: 'Red Bull Event Coverage',
  brand: 'Red Bull',
  status: 'completed',
  deadline: '2024-05-30',
  budget: 8000,
  deliverables: {
    total: 5,
    completed: 5,
    pending: 0
  }
}];
export function CreatorCampaignsPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'all' | 'active' | 'planning' | 'completed'>('all');
  const filteredCampaigns = mockCampaigns.filter(c => filter === 'all' || c.status === filter);
  return <div className="min-h-screen bg-gray-50 pb-12" data-id="element-522">
      <header className="bg-white border-b sticky top-0 z-10" data-id="element-523">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-524">
          <div className="flex items-center" data-id="element-525">
            <Button variant="ghost" size="sm" className="mr-4" onClick={() => navigate('/creator')} data-id="element-526">
              <ArrowLeft className="h-4 w-4 mr-2" data-id="element-527" /> Back
            </Button>
            <h1 className="text-xl font-bold text-gray-900" data-id="element-528">My Campaigns</h1>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-529">
          <div className="flex space-x-8 -mb-px" data-id="element-530">
            <button onClick={() => navigate('/creator')} className="pb-4 text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent" data-id="element-531">
              Dashboard
            </button>
            <button className="pb-4 text-sm font-medium text-blue-600 border-b-2 border-blue-600" data-id="element-532">
              Campaigns
            </button>
            <button onClick={() => navigate('/creator/deliverables')} className="pb-4 text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent" data-id="element-533">
              Deliverables
            </button>
            <button onClick={() => navigate('/creator/brands')} className="pb-4 text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent" data-id="element-534">
              Brands
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-535">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-536">
          <Card data-id="element-537">
            <CardContent className="p-6" data-id="element-538">
              <div className="flex items-center justify-between" data-id="element-539">
                <div data-id="element-540">
                  <p className="text-sm text-gray-500" data-id="element-541">Active Campaigns</p>
                  <p className="text-2xl font-bold text-blue-600" data-id="element-542">
                    {mockCampaigns.filter(c => c.status === 'active').length}
                  </p>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-543">
                  <Briefcase className="h-6 w-6 text-blue-600" data-id="element-544" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-id="element-545">
            <CardContent className="p-6" data-id="element-546">
              <div className="flex items-center justify-between" data-id="element-547">
                <div data-id="element-548">
                  <p className="text-sm text-gray-500" data-id="element-549">Total Earnings</p>
                  <p className="text-2xl font-bold text-green-600" data-id="element-550">
                    €
                    {mockCampaigns.reduce((sum, c) => sum + c.budget, 0).toLocaleString()}
                  </p>
                </div>
                <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center" data-id="element-551">
                  <DollarSign className="h-6 w-6 text-green-600" data-id="element-552" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-id="element-553">
            <CardContent className="p-6" data-id="element-554">
              <div className="flex items-center justify-between" data-id="element-555">
                <div data-id="element-556">
                  <p className="text-sm text-gray-500" data-id="element-557">Completed</p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-558">
                    {mockCampaigns.filter(c => c.status === 'completed').length}
                  </p>
                </div>
                <div className="h-12 w-12 bg-gray-100 rounded-full flex items-center justify-center" data-id="element-559">
                  <CheckCircle2 className="h-6 w-6 text-gray-600" data-id="element-560" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-2 mb-6" data-id="element-561">
          <Button variant={filter === 'all' ? 'secondary' : 'ghost'} size="sm" onClick={() => setFilter('all')} data-id="element-562">
            All
          </Button>
          <Button variant={filter === 'active' ? 'secondary' : 'ghost'} size="sm" onClick={() => setFilter('active')} data-id="element-563">
            Active
          </Button>
          <Button variant={filter === 'planning' ? 'secondary' : 'ghost'} size="sm" onClick={() => setFilter('planning')} data-id="element-564">
            Planning
          </Button>
          <Button variant={filter === 'completed' ? 'secondary' : 'ghost'} size="sm" onClick={() => setFilter('completed')} data-id="element-565">
            Completed
          </Button>
        </div>

        {/* Campaigns List */}
        <div className="space-y-4" data-id="element-566">
          {filteredCampaigns.map(campaign => {
          const statusVariant = campaign.status === 'active' ? 'success' : campaign.status === 'planning' ? 'default' : 'secondary';
          const daysLeft = Math.ceil((new Date(campaign.deadline).getTime() - new Date().getTime()) / (1000 * 3600 * 24));
          const isUrgent = daysLeft > 0 && daysLeft < 7 && campaign.status === 'active';
          return <Card key={campaign.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/creator/campaigns/${campaign.id}`)} data-id="element-567">
                <CardContent className="p-6" data-id="element-568">
                  <div className="flex items-start justify-between mb-4" data-id="element-569">
                    <div className="flex-1" data-id="element-570">
                      <div className="flex items-center space-x-3 mb-2" data-id="element-571">
                        <h3 className="text-lg font-bold text-gray-900" data-id="element-572">
                          {campaign.name}
                        </h3>
                        <Badge variant={statusVariant} className="capitalize" data-id="element-573">
                          {campaign.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500" data-id="element-574">{campaign.brand}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4" data-id="element-575">
                    <div className="flex items-center space-x-2" data-id="element-576">
                      <Calendar className="h-4 w-4 text-gray-400" data-id="element-577" />
                      <div data-id="element-578">
                        <p className="text-xs text-gray-500" data-id="element-579">Deadline</p>
                        <p className={`text-sm font-medium ${isUrgent ? 'text-red-600' : 'text-gray-900'}`} data-id="element-580">
                          {campaign.deadline}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2" data-id="element-581">
                      <DollarSign className="h-4 w-4 text-gray-400" data-id="element-582" />
                      <div data-id="element-583">
                        <p className="text-xs text-gray-500" data-id="element-584">Budget</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-585">
                          €{campaign.budget.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2" data-id="element-586">
                      <CheckCircle2 className="h-4 w-4 text-gray-400" data-id="element-587" />
                      <div data-id="element-588">
                        <p className="text-xs text-gray-500" data-id="element-589">Completed</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-590">
                          {campaign.deliverables.completed}/
                          {campaign.deliverables.total}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2" data-id="element-591">
                      <Clock className="h-4 w-4 text-gray-400" data-id="element-592" />
                      <div data-id="element-593">
                        <p className="text-xs text-gray-500" data-id="element-594">Pending</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-595">
                          {campaign.deliverables.pending}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-4" data-id="element-596">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1" data-id="element-597">
                      <span data-id="element-598">Progress</span>
                      <span data-id="element-599">
                        {Math.round(campaign.deliverables.completed / campaign.deliverables.total * 100)}
                        %
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-600">
                      <div className="bg-blue-600 h-2 rounded-full transition-all" style={{
                    width: `${campaign.deliverables.completed / campaign.deliverables.total * 100}%`
                  }} data-id="element-601" />
                    </div>
                  </div>
                </CardContent>
              </Card>;
        })}
        </div>
      </main>
    </div>;
}